import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';
import ImportOrdersPage from './ImportOrdersPage';

function OrdersPage({ token, viewAsUserId }) {
  const [activeTab, setActiveTab] = useState('EXPORT');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [expandedRows, setExpandedRows] = useState({});     // { orderId: [shipments] }
  const [expandedLoading, setExpandedLoading] = useState({}); // { orderId: true }
  const [form, setForm] = useState({
    customerCode: '', orderNo: '', orderDate: '',
    currency: 'USDINR', fcAmount: '', costRate: '',
    creditDays: '', expectedShipmentDate: '',
    advanceExpectedFc: '', advanceExpectedDate: '', notes: ''
  });

  // Shipment schedule state
  const [multiShipment, setMultiShipment] = useState(false);
  const [shipments, setShipments] = useState([]);   // [{ expectedDate, expectedFc, fcInvoiced }]

  const readOnly = Boolean(viewAsUserId);

  const buildHeaders = () => {
    const h = { authorization: `Bearer ${token}` };
    if (viewAsUserId) h['X-View-As-User-Id'] = String(viewAsUserId);
    return h;
  };

  useEffect(() => {
    loadOrders();
    if (readOnly) {
      setShowForm(false);
      setEditingOrderId(null);
    }
    setExpandedRows({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsUserId]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/orders`, {
        headers: buildHeaders()
      });
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadShipmentsForRow = async (orderId) => {
    if (expandedRows[orderId]) {
      // already expanded - collapse
      setExpandedRows(prev => {
        const copy = { ...prev };
        delete copy[orderId];
        return copy;
      });
      return;
    }
    try {
      setExpandedLoading(prev => ({ ...prev, [orderId]: true }));
      const res = await axios.get(
        `${CONFIG.API}/api/bills/orders/${orderId}/shipments`,
        { headers: buildHeaders() }
      );
      setExpandedRows(prev => ({ ...prev, [orderId]: res.data }));
    } catch (err) {
      alert('Failed to load shipments');
    } finally {
      setExpandedLoading(prev => {
        const copy = { ...prev };
        delete copy[orderId];
        return copy;
      });
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      customerCode: '', orderNo: '', orderDate: '',
      currency: 'USDINR', fcAmount: '', costRate: '',
      creditDays: '', expectedShipmentDate: '',
      advanceExpectedFc: '', advanceExpectedDate: '', notes: ''
    });
    setMultiShipment(false);
    setShipments([]);
    setEditingOrderId(null);
    setShowForm(false);
  };

  const handleEdit = async (order) => {
    setForm({
      customerCode: order.customerCode || '',
      orderNo: order.orderNo || '',
      orderDate: order.orderDate || '',
      currency: order.currency || 'USDINR',
      fcAmount: order.fcAmount || '',
      costRate: order.costRate || '',
      creditDays: order.creditDays || '',
      expectedShipmentDate: order.expectedShipmentDate || '',
      advanceExpectedFc: order.advanceExpectedFc || '',
      advanceExpectedDate: order.advanceExpectedDate || '',
      notes: typeof order.notes === 'string' ? order.notes : ''
    });
    setEditingOrderId(order.orderId);

    // Load shipments if this order has any
    if (order.shipmentCount > 0) {
      try {
        const res = await axios.get(
          `${CONFIG.API}/api/bills/orders/${order.orderId}/shipments`,
          { headers: buildHeaders() }
        );
        const rows = (res.data || []).map(s => ({
          expectedDate: s.expectedDate,
          expectedFc:   s.expectedFc,
          fcInvoiced:   s.fcInvoiced
        }));
        setShipments(rows);
        setMultiShipment(true);
      } catch (err) {
        console.error('Failed to load shipments for edit', err);
        setShipments([]);
        setMultiShipment(false);
      }
    } else {
      setShipments([]);
      setMultiShipment(false);
    }

    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const toggleMultiShipment = (checked) => {
    setMultiShipment(checked);
    if (checked && shipments.length === 0) {
      // start with 2 empty rows so the user immediately understands what to fill
      setShipments([
        { expectedDate: '', expectedFc: '' },
        { expectedDate: '', expectedFc: '' }
      ]);
    }
  };

  const addShipmentRow = () => {
    setShipments([...shipments, { expectedDate: '', expectedFc: '' }]);
  };

  const removeShipmentRow = (index) => {
    if (shipments[index]?.fcInvoiced > 0) {
      alert('This shipment already has invoices and cannot be removed.');
      return;
    }
    setShipments(shipments.filter((_, i) => i !== index));
  };

  const updateShipmentRow = (index, field, value) => {
    const copy = [...shipments];
    copy[index] = { ...copy[index], [field]: value };
    setShipments(copy);
  };

  // Live sum of shipment amounts
  const shipmentSum = shipments.reduce(
    (acc, s) => acc + (parseFloat(s.expectedFc) || 0), 0
  );
  const orderFc = parseFloat(form.fcAmount) || 0;
  const sumMatchesOrder = multiShipment
    ? Math.abs(shipmentSum - orderFc) < 0.01 && shipments.length > 0
    : true;
  const sumDelta = orderFc - shipmentSum;

  const handleSubmit = async () => {
    // Basic order-level validation
    if (!form.customerCode) { alert('Please enter customer code'); return; }
    if (!form.orderNo)      { alert('Please enter order number'); return; }
    if (!form.orderDate)    { alert('Please enter order date'); return; }
    if (!form.fcAmount)     { alert('Please enter FC amount'); return; }
    if (!form.costRate)     { alert('Please enter cost rate'); return; }

    // Shipment-level validation
    if (multiShipment) {
      if (shipments.length < 1) {
        alert('Add at least one shipment row, or untick "Split into multiple shipments".');
        return;
      }
      for (let i = 0; i < shipments.length; i++) {
        const s = shipments[i];
        if (!s.expectedDate) { alert(`Row ${i+1}: enter expected date`); return; }
        if (!s.expectedFc || parseFloat(s.expectedFc) <= 0) {
          alert(`Row ${i+1}: enter a valid FC amount greater than zero`); return;
        }
      }
      if (!sumMatchesOrder) {
        alert(`Shipment total (${shipmentSum.toLocaleString()}) must equal order FC amount (${orderFc.toLocaleString()}).`);
        return;
      }
    }

    try {
      const payload = {
        customerCode: form.customerCode,
        orderNo: form.orderNo,
        orderDate: form.orderDate,
        currency: form.currency,
        fcAmount: parseFloat(form.fcAmount),
        costRate: parseFloat(form.costRate),
        creditDays: parseInt(form.creditDays),
        // When multiShipment is ticked we still keep the first shipment date
        // in expectedShipmentDate for backwards compatibility with old reports.
        expectedShipmentDate: multiShipment
          ? (shipments[0]?.expectedDate || null)
          : (form.expectedShipmentDate || null),
        advanceExpectedFc: form.advanceExpectedFc ? parseFloat(form.advanceExpectedFc) : null,
        advanceExpectedDate: form.advanceExpectedDate || null,
        notes: form.notes || null
      };

      let savedOrderId;

      if (editingOrderId) {
        await axios.put(`${CONFIG.API}/api/bills/orders/${editingOrderId}`, payload, {
          headers: { authorization: `Bearer ${token}` }
        });
        savedOrderId = editingOrderId;
      } else {
        const res = await axios.post(`${CONFIG.API}/api/bills/orders`, payload, {
          headers: { authorization: `Bearer ${token}` }
        });
        savedOrderId = res.data.orderId;
      }

      // Save shipments (or clear them if multiShipment was unchecked)
      const shipmentPayload = {
        shipments: multiShipment
          ? shipments.map(s => ({
              expectedDate: s.expectedDate,
              expectedFc:   parseFloat(s.expectedFc)
            }))
          : []
      };
      await axios.post(
        `${CONFIG.API}/api/bills/orders/${savedOrderId}/shipments`,
        shipmentPayload,
        { headers: { authorization: `Bearer ${token}` } }
      );

      resetForm();
      loadOrders();
    } catch (err) {
      const msg = err?.response?.data || 'Error saving order. Please check all fields.';
      alert(typeof msg === 'string' ? msg : 'Error saving order.');
    }
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Cancel this order?')) return;
    try {
      await axios.put(`${CONFIG.API}/api/bills/orders/${orderId}/cancel`, {}, {
        headers: { authorization: `Bearer ${token}` }
      });
      loadOrders();
    } catch (err) {
      alert('Cannot cancel this order.');
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'OPEN': 'badge-open',
      'PART_INVOICED': 'badge-partial',
      'FULLY_INVOICED': 'badge-done',
      'CANCELLED': 'badge-cancelled'
    };
    return map[status] || 'badge-open';
  };

  const getShipmentStatusBadge = (status) => {
    const map = {
      'OPEN': { bg: '#E0E7FF', color: '#3730A3' },
      'PART_INVOICED': { bg: '#FEF3C7', color: '#78350F' },
      'FULLY_INVOICED': { bg: '#DCFCE7', color: '#14532D' }
    };
    return map[status] || map['OPEN'];
  };

  const filteredOrders = orders.filter(order => {
    const matchStatus = filterStatus === '' || order.status === filterStatus;
    const matchCustomer = filterCustomer === '' ||
      order.customerCode.toLowerCase().includes(filterCustomer.toLowerCase());
    return matchStatus && matchCustomer;
  });

  return (
    <div className="content">

      {readOnly && (
        <div style={{
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: '6px', padding: '8px 14px',
          fontSize: '13px', color: '#1E40AF', marginBottom: '16px'
        }}>
          🔒 <strong>View-only mode</strong> — creating, editing and cancelling are disabled.
        </div>
      )}

      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px',
        borderBottom: '2px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('EXPORT')}
          style={{
            padding: '10px 24px', border: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: '600',
            background: activeTab === 'EXPORT' ? '#1F4E79' : '#f1f5f9',
            color: activeTab === 'EXPORT' ? '#fff' : '#64748b',
            borderRadius: '8px 8px 0 0', marginRight: '4px'
          }}>
          📤 Export Orders
        </button>
        {!readOnly && (
          <button
            onClick={() => setActiveTab('IMPORT')}
            style={{
              padding: '10px 24px', border: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: '600',
              background: activeTab === 'IMPORT' ? '#1F4E79' : '#f1f5f9',
              color: activeTab === 'IMPORT' ? '#fff' : '#64748b',
              borderRadius: '8px 8px 0 0'
            }}>
            📥 Import Orders
          </button>
        )}
      </div>

      {activeTab === 'IMPORT' && !readOnly && (
        <ImportOrdersPage token={token} />
      )}

      {activeTab === 'EXPORT' && <>

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total orders</div>
          <div className="metric-value val-blue">{orders.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-green">
            {orders.filter(o => o.status === 'OPEN').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Part invoiced</div>
          <div className="metric-value val-amber">
            {orders.filter(o => o.status === 'PART_INVOICED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Fully invoiced</div>
          <div className="metric-value val-blue">
            {orders.filter(o => o.status === 'FULLY_INVOICED').length}
          </div>
        </div>
      </div>

      <div className="section-header">
        <div className="section-title">
          {editingOrderId ? 'Edit order' : 'All orders'}
        </div>
        {!readOnly && (
          <button className="btn-primary" onClick={() => {
            if (showForm) { resetForm(); } else { setShowForm(true); }
          }}>
            {showForm ? '✕ Close' : '+ New order'}
          </button>
        )}
      </div>

      {showForm && !readOnly && (
        <div className="form-card" style={{ maxWidth: '720px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingOrderId ? '✏️ Edit order' : '📋 New order'}
          </div>

          {/* Row 1 — Customer + Order no */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Customer code</label>
              <input name="customerCode" value={form.customerCode}
                onChange={handleChange} placeholder="e.g. SMITH-UK" />
            </div>
            <div className="form-field">
              <label>Order no</label>
              <input name="orderNo" value={form.orderNo}
                onChange={handleChange} placeholder="Your reference" />
            </div>
          </div>

          {/* Row 2 — Date, Currency, FC Amount, Cost Rate */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Order date</label>
              <input type="date" name="orderDate" value={form.orderDate}
                onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Currency</label>
              <select name="currency" value={form.currency} onChange={handleChange}>
                <option value="USDINR">USD/INR</option>
                <option value="EURINR">EUR/INR</option>
                <option value="GBPINR">GBP/INR</option>
                <option value="AEDINR">AED/INR</option>
                <option value="SGDINR">SGD/INR</option>
                <option value="AUDINR">AUD/INR</option>
              </select>
            </div>
            <div className="form-field">
              <label>FC amount</label>
              <input type="number" name="fcAmount" value={form.fcAmount}
                onChange={handleChange} placeholder="0" />
            </div>
            <div className="form-field">
              <label>Cost rate</label>
              <input type="number" name="costRate" value={form.costRate}
                onChange={handleChange} placeholder="e.g. 84.50" />
            </div>
          </div>

          {/* Row 3 — Credit days + (Shipment date OR — moved below when multi) + Adv FC + Adv date */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: multiShipment ? '1fr 1fr 1fr' : '1fr 1fr 1fr 1fr',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <div className="form-field">
              <label>Credit days</label>
              <input type="number" name="creditDays" value={form.creditDays}
                onChange={handleChange} placeholder="e.g. 60" />
            </div>
            {!multiShipment && (
              <div className="form-field">
                <label>Shipment date</label>
                <input type="date" name="expectedShipmentDate"
                  value={form.expectedShipmentDate} onChange={handleChange} />
              </div>
            )}
            <div className="form-field">
              <label>Advance FC (opt)</label>
              <input type="number" name="advanceExpectedFc"
                value={form.advanceExpectedFc} onChange={handleChange}
                placeholder="Optional" />
            </div>
            <div className="form-field">
              <label>Advance date (opt)</label>
              <input type="date" name="advanceExpectedDate"
                value={form.advanceExpectedDate} onChange={handleChange} />
            </div>
          </div>

          {/* Checkbox — Split into multiple shipments */}
          <div style={{
            marginBottom: '14px', padding: '10px 12px',
            background: multiShipment ? '#FEF3C7' : '#F8FAFC',
            border: multiShipment ? '1px solid #FCD34D' : '1px solid #E2E8F0',
            borderRadius: '6px',
            transition: 'all 0.2s'
          }}>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              fontSize: '13px', fontWeight: multiShipment ? '600' : '500',
              color: multiShipment ? '#78350F' : '#374151', cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={multiShipment}
                onChange={e => toggleMultiShipment(e.target.checked)}
                style={{ width: '15px', height: '15px', cursor: 'pointer' }}
              />
              📦 Split into multiple shipments
            </label>
            {!multiShipment && (
              <div style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', marginLeft: '23px' }}>
                Tick this if the order will be shipped in more than one instalment.
              </div>
            )}
          </div>

          {/* Shipment schedule table */}
          {multiShipment && (
            <div style={{
              marginBottom: '16px',
              background: '#F8FAFC',
              border: '1px solid #E2E8F0',
              borderRadius: '6px',
              padding: '12px'
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                Shipment schedule
              </div>

              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748B', fontSize: '12px' }}>
                    <th style={{ padding: '4px 6px', width: '28px' }}>#</th>
                    <th style={{ padding: '4px 6px' }}>Expected date</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>FC amount</th>
                    <th style={{ padding: '4px 6px', width: '90px', textAlign: 'center' }}>Invoiced</th>
                    <th style={{ padding: '4px 6px', width: '30px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map((s, i) => {
                    const locked = (s.fcInvoiced || 0) > 0;
                    return (
                      <tr key={i} style={{ borderTop: '1px solid #E2E8F0' }}>
                        <td style={{ padding: '5px 6px', color: '#64748B' }}>{i + 1}</td>
                        <td style={{ padding: '5px 6px' }}>
                          <input
                            type="date"
                            value={s.expectedDate}
                            disabled={locked}
                            onChange={e => updateShipmentRow(i, 'expectedDate', e.target.value)}
                            style={{
                              width: '100%', padding: '4px 6px',
                              border: '1px solid #CBD5E1', borderRadius: '4px',
                              fontSize: '12px',
                              background: locked ? '#F1F5F9' : '#fff'
                            }}
                          />
                        </td>
                        <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                          <input
                            type="number"
                            value={s.expectedFc}
                            disabled={locked}
                            onChange={e => updateShipmentRow(i, 'expectedFc', e.target.value)}
                            placeholder="0"
                            style={{
                              width: '100%', padding: '4px 6px',
                              border: '1px solid #CBD5E1', borderRadius: '4px',
                              fontSize: '12px', textAlign: 'right',
                              background: locked ? '#F1F5F9' : '#fff'
                            }}
                          />
                        </td>
                        <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: '11px', color: '#64748B' }}>
                          {s.fcInvoiced > 0
                            ? Number(s.fcInvoiced).toLocaleString()
                            : '—'}
                        </td>
                        <td style={{ padding: '5px 6px', textAlign: 'center' }}>
                          {!locked && (
                            <button
                              onClick={() => removeShipmentRow(i)}
                              style={{
                                background: 'transparent', border: 'none',
                                color: '#DC2626', cursor: 'pointer',
                                fontSize: '14px', padding: '2px 4px'
                              }}
                              title="Remove row">
                              🗑
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={addShipmentRow}
                  style={{
                    background: 'transparent',
                    border: '1px dashed #94A3B8',
                    borderRadius: '4px',
                    padding: '4px 12px',
                    fontSize: '12px',
                    color: '#64748B',
                    cursor: 'pointer'
                  }}>
                  + Add shipment row
                </button>
              </div>

              {/* Live total row */}
              <div style={{
                marginTop: '10px', paddingTop: '8px',
                borderTop: '1px solid #E2E8F0',
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', fontSize: '13px'
              }}>
                <span style={{ color: '#64748B' }}>Total of shipments</span>
                {orderFc === 0 ? (
                  <span style={{ color: '#94A3B8' }}>Enter FC amount first</span>
                ) : sumMatchesOrder ? (
                  <span style={{ color: '#059669', fontWeight: '600' }}>
                    ✓ {shipmentSum.toLocaleString()} / {orderFc.toLocaleString()}
                  </span>
                ) : (
                  <span style={{ color: '#DC2626', fontWeight: '600' }}>
                    ✗ {shipmentSum.toLocaleString()} / {orderFc.toLocaleString()}
                    {' '}
                    <span style={{ fontWeight: '400' }}>
                      ({sumDelta > 0 ? `${sumDelta.toLocaleString()} short` : `${Math.abs(sumDelta).toLocaleString()} over`})
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows={3}
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={multiShipment && !sumMatchesOrder}
              style={{
                opacity: (multiShipment && !sumMatchesOrder) ? 0.5 : 1,
                cursor:  (multiShipment && !sumMatchesOrder) ? 'not-allowed' : 'pointer'
              }}>
              {editingOrderId ? '✓ Update order' : '✓ Save order'}
            </button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }}
        />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}
        >
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="PART_INVOICED">Part invoiced</option>
          <option value="FULLY_INVOICED">Fully invoiced</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        {(filterStatus !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterStatus(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredOrders.length} of {orders.length} orders
        </span>
      </div>

      {/* Orders table */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredOrders.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No orders found. Try changing the filters.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Order no</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Currency</th>
                <th>FC amount</th>
                <th>Cost rate</th>
                <th>Shipment date</th>
                <th>FC balance</th>
                <th>Status</th>
                {!readOnly && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => {
                const hasMulti = (order.shipmentCount || 0) > 0;
                const isExpanded = Boolean(expandedRows[order.orderId]);
                const rowBg = hasMulti ? '#FFFBEB' : undefined;
                return (
                  <React.Fragment key={order.orderId}>
                    <tr style={{ background: rowBg }}>
                      <td>{order.orderNo}</td>
                      <td>{order.customerCode}</td>
                      <td>{order.orderDate}</td>
                      <td>{order.currency}</td>
                      <td>{Number(order.fcAmount).toLocaleString()}</td>
                      <td>{Number(order.costRate).toFixed(4)}</td>
                      <td>
                        {order.expectedShipmentDate || '—'}
                        {hasMulti && (
                          <span
                            onClick={() => loadShipmentsForRow(order.orderId)}
                            style={{
                              display: 'inline-block',
                              marginLeft: '6px',
                              background: '#FEF3C7',
                              color: '#78350F',
                              padding: '1px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}>
                            {isExpanded ? '▲ hide' : `+${order.shipmentCount} more`}
                          </span>
                        )}
                      </td>
                      <td>{Number(order.fcBalance).toLocaleString()}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      {!readOnly && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            className="btn-secondary"
                            onClick={() => handleEdit(order)}
                            style={{ marginRight: '6px' }}>
                            Edit
                          </button>
                          {order.status === 'OPEN' && (
                            <button
                              className="btn-danger"
                              onClick={() => handleCancel(order.orderId)}>
                              Cancel
                            </button>
                          )}
                        </td>
                      )}
                    </tr>

                    {isExpanded && (
                      <tr style={{ background: rowBg }}>
                        <td colSpan={readOnly ? 9 : 10} style={{ padding: '4px 10px 10px 40px' }}>
                          <div style={{
                            background: '#fff',
                            border: '1px solid #E2E8F0',
                            borderRadius: '6px',
                            padding: '8px 12px'
                          }}>
                            {expandedLoading[order.orderId] ? (
                              <span style={{ color: '#64748B', fontSize: '12px' }}>Loading shipments...</span>
                            ) : (
                              <>
                                <div style={{ fontSize: '11px', color: '#64748B', marginBottom: '6px', fontWeight: '600' }}>
                                  Shipment schedule ({expandedRows[order.orderId]?.length || 0} rows)
                                </div>
                                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ color: '#64748B', textAlign: 'left' }}>
                                      <th style={{ padding: '3px 6px', fontWeight: '400', width: '20px' }}>#</th>
                                      <th style={{ padding: '3px 6px', fontWeight: '400' }}>Expected date</th>
                                      <th style={{ padding: '3px 6px', fontWeight: '400', textAlign: 'right' }}>Expected FC</th>
                                      <th style={{ padding: '3px 6px', fontWeight: '400', textAlign: 'right' }}>Invoiced</th>
                                      <th style={{ padding: '3px 6px', fontWeight: '400' }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(expandedRows[order.orderId] || []).map((s, i) => {
                                      const b = getShipmentStatusBadge(s.status);
                                      return (
                                        <tr key={s.shipmentId} style={{ borderTop: '1px solid #E2E8F0' }}>
                                          <td style={{ padding: '3px 6px', color: '#64748B' }}>{s.sequenceNo}</td>
                                          <td style={{ padding: '3px 6px' }}>{s.expectedDate}</td>
                                          <td style={{ padding: '3px 6px', textAlign: 'right' }}>{Number(s.expectedFc).toLocaleString()}</td>
                                          <td style={{ padding: '3px 6px', textAlign: 'right' }}>{Number(s.fcInvoiced).toLocaleString()}</td>
                                          <td style={{ padding: '3px 6px' }}>
                                            <span style={{
                                              background: b.bg, color: b.color,
                                              padding: '1px 6px', borderRadius: '8px',
                                              fontSize: '10px', fontWeight: '600'
                                            }}>
                                              {s.status}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      </>}
    </div>
  );
}

export default OrdersPage;